import { toast } from "sonner";
import {
  useCreateScheduleSlot,
  useDeleteScheduleSlot,
  useSetScheduleRelease,
  useSetScheduleLock,
  useUpdateScheduleSlot,
} from "@/api/hooks/use-schedule";
import type { EventId, ScheduleSlotId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type SlotCreate = components["schemas"]["ScheduleSlotCreate"];
type SlotUpdate = components["schemas"]["ScheduleSlotUpdate"];

export interface ScheduleActions {
  /** Resolves on success; rejects on failure so the dialog can stay open for retry. */
  handleCreate: (data: SlotCreate) => Promise<void>;
  handleUpdate: (id: ScheduleSlotId, data: SlotUpdate) => Promise<void>;
  handleDelete: (id: ScheduleSlotId) => Promise<void>;
  /** Show or hide the board for viewers. Withdrawal is the escape hatch for
   * a board released with the wrong times. */
  handleSetRelease: (released: boolean) => Promise<void>;
  /** Reversible: freezes the plan against slot edits. */
  handleSetLock: (locked: boolean) => Promise<void>;
  isSaving: boolean;
  isReleasing: boolean;
  isLocking: boolean;
}

export function useScheduleActions(eventId: EventId): ScheduleActions {
  const create = useCreateScheduleSlot(eventId);
  const update = useUpdateScheduleSlot(eventId);
  const del = useDeleteScheduleSlot(eventId);
  const release = useSetScheduleRelease(eventId);
  const lock = useSetScheduleLock(eventId);

  return {
    handleCreate: (data) =>
      create.mutateAsync(data).then(
        () => {
          toast.success("Slot scheduled");
        },
        (err: Error) => {
          toast.error(err.message);
          throw err;
        },
      ),
    handleUpdate: (id, data) =>
      update.mutateAsync({ slotId: id, data }).then(
        () => {
          toast.success("Slot updated");
        },
        (err: Error) => {
          toast.error(err.message);
          throw err;
        },
      ),
    handleDelete: (id) =>
      del.mutateAsync(id).then(
        () => {
          toast.success("Slot removed");
        },
        (err: Error) => {
          toast.error(err.message);
          throw err;
        },
      ),
    handleSetRelease: (released) =>
      release.mutateAsync(released).then(
        () => {
          toast.success(released ? "Schedule released to teams" : "Schedule withdrawn");
        },
        (err: Error) => {
          toast.error(err.message);
          throw err;
        },
      ),
    handleSetLock: (locked) =>
      lock.mutateAsync(locked).then(
        () => {
          toast.success(locked ? "Schedule frozen" : "Schedule unfrozen");
        },
        (err: Error) => {
          toast.error(err.message);
          throw err;
        },
      ),
    isSaving: create.isPending || update.isPending || del.isPending,
    isReleasing: release.isPending,
    isLocking: lock.isPending,
  };
}
